import { addPagesFromContext, addWidgetsFromContext } from "@insite/client-framework/Configuration";
import { setPostStyleGuideTheme, setPreStyleGuideTheme } from "@insite/client-framework/ThemeConfiguration";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import { css } from "styled-components";

// Load all custom state objects so they are included in the bundle.
const store = require.context("./Store", true);
store.keys().forEach(key => store(key));

// Load all custom widgets so they are included in the bundle and enable hot module reloading for them. Do not do this for the ./Overrides folder
const widgets = require.context("./Widgets", true, /\.tsx$/);
const onHotWidgetReplace = addWidgetsFromContext(widgets);
if (module.hot) {
    module.hot.accept(widgets.id, () => onHotWidgetReplace(require.context("./Widgets", true, /\.tsx$/)));
}

// Load all custom pages so they are included in the bundle and enable hot module reloading for them. Do not do this for the ./Overrides folder
const pages = require.context("./Pages", true, /\.tsx$/);
const onHotPageReplace = addPagesFromContext(pages);
if (module.hot) {
    module.hot.accept(pages.id, () => onHotPageReplace(require.context("./Pages", true, /\.tsx$/)));
}

// Load all handlers so they are included in the bundle.
const handlers = require.context("./Handlers", true);
handlers.keys().forEach(key => handlers(key));

// Load all the extensions to the base widgets. This includes style extensions.

const widgetExtensions = require.context("./WidgetExtensions", true);
widgetExtensions.keys().forEach(key => widgetExtensions(key));

setPreStyleGuideTheme({
    breakpoints: {
        keys: ["xs", "sm", "md", "lg", "xl"],
        values: [0, 576, 768, 992, 1200],
        maxWidths: [540, 540, 720, 960, 1440],
    },
    typography: {
        fontFamilyImportUrl:
            "https://fonts.googleapis.com/css?family=Roboto:300,400,500,600,700,800|Lato:300,400,500,600,700,800&display=block",
        body: {
            size: themeFontSizes.size15,
            weight: 400,
            fontFamily: "'Roboto', sans-serif",
        },
        p: {
            size: themeFontSizes.size15,
            weight: 300,
            css: css`
                margin-bottom: 1rem;
                display: block;
            `,
        },
        h1: {
            size: themeFontSizes.h1Size,
            weight: 700,
            css: css`
                margin-bottom: 1rem;
                display: block;
            `,
        },
        h2: {
            size: themeFontSizes.h2Size,
            weight: 700,
            css: css`
                margin-bottom: 1rem;
                display: block;
            `,
        },
        h3: {
            size: themeFontSizes.h3Size,
            weight: 700,
            css: css`
                margin-bottom: 1rem;
                display: block;
            `,
        },
        h4: {
            size: themeFontSizes.h4Size,
            weight: 700,
            css: css`
                margin-bottom: 1rem;
                display: block;
            `,
        },
        h5: {
            size: themeFontSizes.h5Size,
            weight: 700,
            css: css`
                margin-bottom: 1rem;
                display: block;
            `,
        },
        h6: {
            size: themeFontSizes.h6Size,
            weight: 700,
            css: css`
                margin-bottom: 1rem;
                display: block;
            `,
        },
    },
    focus: {
        color: "#09f",
        style: "solid",
        width: ".0625rem",
    },
    globalStyle: {
        css: css`
            @media screen and (min-width: 1439px) {
                [data-test-selector="homePage"] .is-draggable {
                    height: 31.875rem;
                }
            }
            @media screen and (min-width: 768px) and (max-width: 1024px) {
                [data-test-selector="homePage"] .is-selected{
                    min-height: 100vw;
                }
                [data-test-selector="homePage"] .is-draggable {
                    height: initial;
                }
            }
            @media (max-width: 767px) {
                [data-test-selector="homePage"] .is-draggable {
                    height: initial;
                }
            }
        `},
    colors: {
        primary: {
            main: themeColors.colorPrimary,
            contrast: themeColors.colorWhite,
        },
        secondary: {
            main: themeColors.colorSecondary,
            contrast: themeColors.colorPrimary,
        },
        common: {
            background: themeColors.colorWhite,
            backgroundContrast: themeColors.colorPrimary,
            accent: themeColors.colorBackgroundLightGrey,
            accentContrast: themeColors.colorWhite,
            border: themeColors.colorPrimary,
            disabled: themeColors.colorGrey,
        },
        text: {
            main: themeColors.colorPrimary,
            disabled: themeColors.colorDisabled,
            accent: themeColors.colorAccent,
            link: themeColors.colorLink,
        },
        success: {
            main: themeColors.colorSuccess,
            contrast: themeColors.colorPrimary,
        },
        danger: {
            main: themeColors.colorDanger,
            contrast: themeColors.colorPrimary,
        },
        warning: {
            main: themeColors.colorWarning,
            contrast: themeColors.colorPrimary,
        },
        info: {
            main: themeColors.colorInfo,
            contrast: themeColors.colorPrimary,
        },
    },
    button: {
        primary: {
            color: "secondary",
            buttonType: "solid",
            hoverStyle: {
                background: themeColors.colorPrimaryButtonHover,
                border: "2px solid #FF9400",
            },
            activeStyle: {
                background: themeColors.colorPrimaryButtonActive,
                border: "2px solid #AB6300",
                outline: "0",
            },
            css: css`
                &:focus:not(:focus-visible) {
                    outline: none !important;
                }
                &:focus-visible {
                    outline: 2px dashed #2060ad !important;
                }
                &:active {
                    outline: none !important;
                }
            `,
        },
        secondary: {
            color: "primary",
            buttonType: "outline",
            hoverStyle: {
                background: themeColors.colorButtonHover,
                border: "2px solid #2060ad",
                color: themeColors.colorWhite,
            },
            activeStyle: {
                background: themeColors.colorButtonActive,
                border: "2px solid #002554",
                color: themeColors.colorWhite,
                outline: "0",
            },
            css: css`
                border: 2px solid #002554;
                &:focus:not(:focus-visible) {
                    outline: none !important;
                }
                &:focus-visible {
                    outline: 2px dashed #2060ad !important;
                }
                &:active {
                    outline: none !important;
                }
            `,
        },
    },
});
